import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, Card, DatePicker, Form, Input, message, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Dropdown, Menu } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import axios from '../../api';
import theme from '../../theme';
import { formatApiError } from '../../utils/apiError';
import { EllipsisOutlined } from '@ant-design/icons';

// 生成符合规则的随机密码
const generateRandomPassword = () => {
  const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  
  // 确保至少包含一个字母和一个数字
  let password = '';
  password += letters[Math.floor(Math.random() * letters.length)]; // 添加一个字母
  password += numbers[Math.floor(Math.random() * numbers.length)]; // 添加一个数字
  
  // 添加剩余的随机字符（至少6个，总共8位）
  const allChars = letters + numbers;
  for (let i = 0; i < 6; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // 打乱密码字符顺序
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

const { Search } = Input;
const { RangePicker } = DatePicker;

const MEMBER_SUBMIT_FIELDS = [
  'username', 'email', 'first_name', 'last_name', 'student_no', 'role', 'valid_until',
  'is_active', 'password', 'study_status', 'study_start_date', 'study_end_date',
  'graduation_date', 'suspension_reason', 'dropout_reason', 'avatar',
  'google_email', 'google_calendar_id', 'wecom_user_id', 'wecom_external_userid',
  'whatsapp_phone', 'language', 'timezone', 'group_id',
];

const buildMemberSubmitData = (values, editingMember) => {
  const submitData = {};
  MEMBER_SUBMIT_FIELDS.forEach((field) => {
    if (values[field] !== undefined) {
      submitData[field] = values[field];
    }
  });

  if (values.valid_until) submitData.valid_until = values.valid_until.format('YYYY-MM-DD');
  if (values.study_start_date) submitData.study_start_date = values.study_start_date.format('YYYY-MM-DD');
  if (values.study_end_date) submitData.study_end_date = values.study_end_date.format('YYYY-MM-DD');
  if (values.graduation_date) submitData.graduation_date = values.graduation_date.format('YYYY-MM-DD');

  if (editingMember && !submitData.password) {
    delete submitData.password;
  }

  return submitData;
};



const MemberList = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingMember, setEditingMember] = useState(null);
  const [groups, setGroups] = useState([]);
  const [documentGroups, setDocumentGroups] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [error, setError] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [wecomPickerVisible, setWecomPickerVisible] = useState(false);
  const [wecomContacts, setWecomContacts] = useState([]);
  const [wecomContactsLoading, setWecomContactsLoading] = useState(false);
  const [wecomSearchText, setWecomSearchText] = useState('');
  const [wecomMembersPickerVisible, setWecomMembersPickerVisible] = useState(false);
  const [wecomMembers, setWecomMembers] = useState([]);
  const [wecomMembersLoading, setWecomMembersLoading] = useState(false);
  const [wecomMembersSearchText, setWecomMembersSearchText] = useState('');

  // 拉取企微客户列表（使用 WECOM_CONTACT_SENDER_USERID）
  const fetchWecomContacts = async () => {
    try {
      setWecomContactsLoading(true);
      const res = await axios.get('/api/users/wecom-contacts/');
      setWecomContacts(res.data.contacts || []);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || '拉取企微客户列表失败';
      message.error(msg);
      setWecomContacts([]);
    } finally {
      setWecomContactsLoading(false);
    }
  };

  const openWecomPicker = () => {
    setWecomSearchText('');
    setWecomPickerVisible(true);
    fetchWecomContacts();
  };

  const selectWecomContact = (item) => {
    form.setFieldsValue({ wecom_external_userid: item.external_userid });
    setWecomPickerVisible(false);
    message.success(`已选择: ${item.name || item.remark || item.external_userid}`);
  };

  const filteredWecomContacts = wecomSearchText.trim()
    ? wecomContacts.filter((c) => {
        const t = wecomSearchText.toLowerCase();
        const name = (c.name || '').toLowerCase();
        const remark = (c.remark || '').toLowerCase();
        const corp = (c.corp_name || '').toLowerCase();
        const ext = (c.external_userid || '').toLowerCase();
        return name.includes(t) || remark.includes(t) || corp.includes(t) || ext.includes(t);
      })
    : wecomContacts;

  // 拉取企微成员列表（通讯录员工，用于老师等）
  const fetchWecomMembers = async () => {
    try {
      setWecomMembersLoading(true);
      const res = await axios.get('/api/users/wecom-members/');
      setWecomMembers(res.data.members || []);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || '拉取企微成员列表失败';
      message.error(msg);
      setWecomMembers([]);
    } finally {
      setWecomMembersLoading(false);
    }
  };

  const openWecomMembersPicker = () => {
    setWecomMembersSearchText('');
    setWecomMembersPickerVisible(true);
    fetchWecomMembers();
  };

  const selectWecomMember = (item) => {
    form.setFieldsValue({ wecom_user_id: item.userid });
    setWecomMembersPickerVisible(false);
    message.success(`已选择: ${item.name || item.userid}`);
  };

  const filteredWecomMembers = wecomMembersSearchText.trim()
    ? wecomMembers.filter((m) => {
        const t = wecomMembersSearchText.toLowerCase();
        const name = (m.name || '').toLowerCase();
        const userid = (m.userid || '').toLowerCase();
        return name.includes(t) || userid.includes(t);
      })
    : wecomMembers;

  // 获取用户组列表
  const fetchGroups = async () => {
    try {
      console.log('开始获取用户组列表...');
      const response = await axios.get('/api/users/groups/');
      console.log('用户组列表响应:', response.data);
      setGroups(response.data);
    } catch (error) {
      console.error('获取用户组列表失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      message.error('获取用户组列表失败: ' + (error.response?.data?.detail || error.message));
      setGroups([]);
    }
  };

  // 获取文档组列表
  const fetchDocumentGroups = async () => {
    try {
      console.log('开始获取用户可访问的文档组列表...');
      // 使用新的API接口获取用户可访问的文档组
      const response = await axios.get('/api/user-accessible-document-groups/');
      console.log('用户可访问的文档组列表响应:', response.data);
      setDocumentGroups(response.data.results || response.data || []);
    } catch (error) {
      console.error('获取文档组列表失败:', error);
      message.error('获取文档组列表失败: ' + (error.response?.data?.detail || error.message));
      setDocumentGroups([]);
    }
  };

  // 获取会员列表
  const fetchMembers = async (page = 1, size = 10, search = '') => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page,
        page_size: size,
        search
      };

      if (dateRange) {
        params.reg_start = dateRange[0].format('YYYY-MM-DD');
        params.reg_end = dateRange[1].format('YYYY-MM-DD');
      }

      console.log('开始获取会员列表，参数:', params);
      console.log('请求头:', axios.defaults.headers);

      const response = await axios.get('/api/users/', { params });
      console.log('会员列表响应:', response.data);

      if (Array.isArray(response.data)) {
        setMembers(response.data);
        setTotal(response.data.length);
      } else if (response.data && Array.isArray(response.data.results)) {
        setMembers(response.data.results);
        setTotal(response.data.count);
      } else {
        console.error('响应数据格式不正确:', response.data);
        message.error('数据格式错误');
        setError('数据格式错误');
      }
    } catch (error) {
      console.error('获取会员列表失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });

      const errorMessage = formatApiError(error, '获取会员列表失败');
      message.error(errorMessage);
      setError(errorMessage);
      setMembers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers(currentPage, pageSize, searchText);
    fetchGroups();
    fetchDocumentGroups();
  }, [currentPage, pageSize, searchText, dateRange]);

  // 处理搜索
  const handleSearch = (value) => {
    console.log('搜索关键词:', value);
    setSearchText(value);
    setCurrentPage(1);
  };

  // 处理日期范围变化
  const handleDateRangeChange = (dates) => {
    console.log('日期范围变化:', dates);
    setDateRange(dates);
    setCurrentPage(1);
  };

  // 处理分页
  const handleTableChange = (pagination) => {
    console.log('分页变化:', pagination);
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // 处理新增/编辑
  const handleAddEdit = (record = null) => {
    console.log('打开编辑模态框:', record);
    setEditingMember(record);
    if (record) {
      form.setFieldsValue({
        ...record,
        valid_until: record.valid_until ? dayjs(record.valid_until) : null,
        is_active: record.is_active === undefined ? true : record.is_active,
        study_start_date: record.study_start_date ? dayjs(record.study_start_date) : null,
        study_end_date: record.study_end_date ? dayjs(record.study_end_date) : null,
        graduation_date: record.graduation_date ? dayjs(record.graduation_date) : null,
        // 编辑时不设置密码字段，让用户看到占位符
      });
    } else {
      form.resetFields();
      // 设置默认值（学生默认中文，老师默认法语）
      form.setFieldsValue({
        is_active: true,
        role: 'student',
        study_status: 'studying',
        password: generateRandomPassword(), // 使用符合规则的随机密码
        language: 'zh-CN', // 学生默认中文
      });
    }
    setModalVisible(true);
  };

  // 处理表单提交
  const handleSubmit = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      message.error('登录已过期，请重新登录');
      window.location.replace('/login');
      return;
    }

    try {
      const values = await form.validateFields();
      const submitData = buildMemberSubmitData(values, editingMember);

      if (editingMember) {
        await axios.patch(`/api/users/${editingMember.id}/`, submitData);
        message.success('更新成功');
        setModalVisible(false);
        fetchMembers(currentPage, pageSize, searchText);
      } else {
        await axios.post('/api/users/', submitData);
        message.success('添加成功');
        setModalVisible(false);
        setCurrentPage(1);
        setSearchText('');
        setTimeout(() => {
          fetchMembers(1, pageSize, '');
        }, 0);
      }
    } catch (error) {
      const fallback = editingMember ? '更新用户失败' : '新增用户失败';
      message.error(formatApiError(error, fallback));
      throw error;
    }
  };

  // 处理删除
  const handleDelete = async (id) => {
    try {
      console.log('删除会员:', id);
      await axios.delete(`/api/users/${id}/`);
      console.log('删除成功');
      message.success('删除成功');
      fetchMembers(currentPage, pageSize, searchText);
    } catch (error) {
      console.error('删除失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      message.error(formatApiError(error, '删除用户失败'));
    }
  };

  // 处理批量删除
  const handleBatchDelete = async () => {
    try {
      console.log('批量删除会员:', selectedRowKeys);
      await Promise.all(selectedRowKeys.map(id => axios.delete(`/api/users/${id}/`)));
      message.success('批量删除成功');
      setSelectedRowKeys([]);
      fetchMembers(currentPage, pageSize, searchText);
    } catch (error) {
      console.error('批量删除失败:', error);
      message.error('批量删除失败');
    }
  };

  // 检查会员状态
  const checkMemberStatus = (validUntil) => {
    if (!validUntil) return { status: 'default', text: '未设置', tooltip: '未设置有效期', color: '#999' };
    const now = dayjs();
    const validDate = dayjs(validUntil);
    if (validDate.isBefore(now)) {
      return { status: 'error', text: '已过期', tooltip: '会员已过期', color: '#ff4d4f' };
    }
    if (validDate.diff(now, 'day') <= 7) {
      return { status: 'warning', text: '即将过期', tooltip: '会员即将过期', color: '#faad14' };
    }
    return { status: 'success', text: '有效', tooltip: '会员有效', color: '#52c41a' };
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '姓名',
      dataIndex: 'first_name',
      key: 'first_name',
      render: (firstName) => firstName || '-',
    },
    {
      title: '学号',
      dataIndex: 'student_no',
      key: 'student_no',
      width: 120,
      render: (v) => v || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '企微成员',
      dataIndex: 'wecom_user_id',
      key: 'wecom_user_id',
      width: 80,
      ellipsis: true,
      render: (v) => v ? '✓' : '—',
    },
    {
      title: '企微客户',
      dataIndex: 'wecom_external_userid',
      key: 'wecom_external_userid',
      width: 150,
      ellipsis: true,
      render: (v) => {
        if (!v) return '—';
        // 判断是否是 External UserID 格式（长度>=20）
        const isExternalUserId = v.length >= 20 && !v.includes('wxid');
        return (
          <Tooltip title={v}>
            <span>
              {isExternalUserId ? (
                <Tag color="green">已转换</Tag>
              ) : (
                <Tag color="orange">微信号</Tag>
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'WhatsApp',
      dataIndex: 'whatsapp_phone',
      key: 'whatsapp_phone',
      width: 90,
      ellipsis: true,
      render: (v) => v ? '✓' : '—',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        const roleMap = {
          'student': '学生',
          'teacher': '老师',
          'admin': '教务',
          'sysadmin': '系统管理员'
        };
        return roleMap[role] || role;
      },
    },
    {
      title: '学习状态',
      dataIndex: 'study_status',
      key: 'study_status',
      render: (studyStatus, record) => {
        if (record.role !== 'student') return '-';
        const statusMap = {
          'studying': { text: '学习中', color: 'green' },
          'graduated': { text: '已毕业', color: 'blue' },
          'dropped': { text: '退学', color: 'red' },
          'suspended': { text: '暂停', color: 'orange' },
          'completed': { text: '已完成', color: 'purple' },
        };
        const status = statusMap[studyStatus] || { text: studyStatus, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '有效期',
      dataIndex: 'valid_until',
      key: 'valid_until',
      render: (validUntil) => {
        if (!validUntil) return '-';
        const status = checkMemberStatus(validUntil);
        return (
          <Tooltip title={status.tooltip}>
            <span style={{ color: status.color }}>
              {dayjs(validUntil).format('YYYY-MM-DD')}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '注册时间',
      dataIndex: 'date_joined',
      key: 'date_joined',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="edit" onClick={() => handleAddEdit(record)}>
              <EditOutlined /> 编辑
            </Menu.Item>
            <Menu.Item key="delete">
              <Popconfirm
                title="确定要删除这个会员吗？"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <span style={{ color: 'red' }}><DeleteOutlined /> 删除</span>
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
        boxSizing: 'border-box',
      }}
    >
      <Card
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
        bodyStyle={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <RangePicker
              onChange={handleDateRangeChange}
              value={dateRange}
              placeholder={['开始日期', '结束日期']}
            />
            <Search
              placeholder="搜索会员"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              style={{ width: 300 }}
            />
          </Space>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchMembers(currentPage, pageSize, searchText)}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleAddEdit()}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              新增会员
            </Button>
          </Space>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Table
            columns={columns}
            dataSource={members}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      <Modal
        title={editingMember ? '编辑会员' : '新增会员'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        destroyOnHidden
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="first_name"
            label="姓名"
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select 
              placeholder="选择角色"
              onChange={(value) => {
                if (!editingMember) {
                  const defaultLanguage = value === 'teacher' ? 'fr-FR' : 'zh-CN';
                  form.setFieldsValue({ language: defaultLanguage });
                }
                if (value !== 'student') {
                  form.setFieldsValue({ student_no: undefined });
                }
              }}
            >
              <Select.Option value="student">学生</Select.Option>
              <Select.Option value="teacher">老师</Select.Option>
              <Select.Option value="admin">教务</Select.Option>
              <Select.Option value="manager">管理员</Select.Option>
              <Select.Option value="superadmin">超级管理员</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
          >
            {({ getFieldValue }) => {
              const role = getFieldValue('role');
              if (role !== 'student') return null;
              return (
                <Form.Item
                  name="student_no"
                  label="学号"
                  extra="用于智能体对话身份识别（如 Dify 的 student_id），留空则不传入"
                  rules={[
                    { max: 64, message: '学号长度不能超过 64 个字符' },
                    { pattern: /^[A-Za-z0-9_-]*$/, message: '学号仅支持字母、数字、下划线和连字符' },
                  ]}
                >
                  <Input placeholder="选填，如 2024001" allowClear />
                </Form.Item>
              );
            }}
          </Form.Item>
          {/* 老师/员工角色填企业微信成员 UserID，学生角色填企微客户ID/微信号 */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
          >
            {({ getFieldValue }) => {
              const role = getFieldValue('role');
              if (role === 'teacher' || role === 'admin' || role === 'manager' || role === 'superadmin') {
                return (
                  <Form.Item
                    name="wecom_user_id"
                    label="企业微信成员 UserID"
                    extra="给企业员工本人发消息时使用，可从企微通讯录选择"
                  >
                    <Input
                      placeholder="选填，如 e-artxue"
                      addonAfter={
                        <Button
                          type="link"
                          size="small"
                          icon={<TeamOutlined />}
                          onClick={openWecomMembersPicker}
                          style={{ padding: 0, height: 'auto' }}
                        >
                          从企微选择
                        </Button>
                      }
                    />
                  </Form.Item>
                );
              }
              if (role === 'student') {
                return (
                  <Form.Item
                    name="wecom_external_userid"
                    label="企微客户ID/微信号"
                    extra="填写微信号（如：ToNewLife7）或企微 External UserID；可从企微客户列表选择"
                  >
                    <Input
                      placeholder="填写微信号或 External UserID，如：ToNewLife7"
                      addonAfter={
                        <Button
                          type="link"
                          size="small"
                          icon={<TeamOutlined />}
                          onClick={openWecomPicker}
                          style={{ padding: 0, height: 'auto' }}
                        >
                          从企微选择
                        </Button>
                      }
                    />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item
            name="whatsapp_phone"
            label="WhatsApp 手机号"
            extra="用于课程提醒 WhatsApp 推送，E.164 格式如 8613800138000"
          >
            <Input placeholder="选填，国家码+号码" />
          </Form.Item>
          <Form.Item
            name="google_email"
            label="Google 邮箱"
            extra="用于日历邀请与通知"
          >
            <Input placeholder="选填，如 user@gmail.com" />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
          >
            {({ getFieldValue }) => {
              const role = getFieldValue('role');
              if (role === 'student') return null;
              return (
                <Form.Item
                  name="google_calendar_id"
                  label="Google 日历 ID"
                  extra="若已配置，排课同步时使用该日历，不新建；老师角色常用"
                >
                  <Input placeholder="选填，如 xxx@group.calendar.google.com" />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            name="language"
            label="语言环境"
            extra="用于通知消息的语言模板选择（老师默认法语，学生默认中文）"
          >
            <Select placeholder="选择语言">
              <Select.Option value="zh-CN">中文</Select.Option>
              <Select.Option value="en-US">English</Select.Option>
              <Select.Option value="fr-FR">Français</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="timezone"
            label="时区"
            extra="推送消息中的上课时间将按此时区显示；不填则按北京时间"
          >
            <Select placeholder="选择时区（不选即北京时间）" allowClear showSearch optionFilterProp="label">
              <Select.Option value="Asia/Shanghai" label="北京时间 (Asia/Shanghai)">北京时间 (Asia/Shanghai)</Select.Option>
              <Select.Option value="Europe/Paris" label="巴黎 (Europe/Paris)">巴黎 (Europe/Paris)</Select.Option>
              <Select.Option value="Europe/London" label="伦敦 (Europe/London)">伦敦 (Europe/London)</Select.Option>
              <Select.Option value="America/New_York" label="纽约 (America/New_York)">纽约 (America/New_York)</Select.Option>
              <Select.Option value="America/Los_Angeles" label="洛杉矶 (America/Los_Angeles)">洛杉矶 (America/Los_Angeles)</Select.Option>
              <Select.Option value="Asia/Tokyo" label="东京 (Asia/Tokyo)">东京 (Asia/Tokyo)</Select.Option>
              <Select.Option value="Australia/Sydney" label="悉尼 (Australia/Sydney)">悉尼 (Australia/Sydney)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="password"
            label={editingMember ? "新密码（留空则不修改）" : "密码"}
            rules={[
              { required: !editingMember, message: '请输入密码' },
              { min: 8, message: '密码长度至少8位' },
              {
                pattern: /^(?=.*[a-zA-Z])(?=.*\d)[A-Za-z\d]{8,}$/,
                message: '密码必须包含字母和数字，长度至少8位'
              }
            ]}
            extra="密码必须包含字母和数字，长度至少8位"
          >
            {/* 新增会员时明文显示密码，编辑时为密码框 */}
            {editingMember ? (
              <Input.Password placeholder="输入新密码或留空" />
            ) : (
              <Input autoComplete="new-password" />
            )}
          </Form.Item>

          {/* 学习状态相关字段 - 仅对学生显示 */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
          >
            {({ getFieldValue }) => {
              const role = getFieldValue('role');
              if (role === 'student') {
                return (
                  <>
                    <Form.Item
                      name="study_status"
                      label="学习状态"
                      rules={[{ required: true, message: '请选择学习状态' }]}
                    >
                      <Select>
                        <Select.Option value="studying">学习中</Select.Option>
                        <Select.Option value="graduated">已毕业</Select.Option>
                        <Select.Option value="dropped">退学</Select.Option>
                        <Select.Option value="suspended">暂停</Select.Option>
                        <Select.Option value="completed">已完成</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => prevValues.study_status !== currentValues.study_status}
                    >
                      {({ getFieldValue }) => {
                        const studyStatus = getFieldValue('study_status');
                        if (studyStatus === 'suspended') {
                          return (
                            <Form.Item
                              name="suspension_reason"
                              label="暂停原因"
                              rules={[{ required: true, message: '请输入暂停原因' }]}
                            >
                              <Input.TextArea rows={3} placeholder="请输入暂停原因" />
                            </Form.Item>
                          );
                        }
                        if (studyStatus === 'dropped') {
                          return (
                            <Form.Item
                              name="dropout_reason"
                              label="退学原因"
                              rules={[{ required: true, message: '请输入退学原因' }]}
                            >
                              <Input.TextArea rows={3} placeholder="请输入退学原因" />
                            </Form.Item>
                          );
                        }
                        return null;
                      }}
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="is_active"
            label="状态"
          >
            <Select>
              <Select.Option value={true}>启用</Select.Option>
              <Select.Option value={false}>禁用</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="从企微客户列表选择"
        open={wecomPickerVisible}
        onCancel={() => setWecomPickerVisible(false)}
        footer={null}
        width={520}
        zIndex={1100}
      >
        <Input.Search
          placeholder="搜索姓名、备注、企业名"
          value={wecomSearchText}
          onChange={(e) => setWecomSearchText(e.target.value)}
          style={{ marginBottom: 12 }}
          allowClear
        />
        <div style={{ maxHeight: 320, overflow: 'auto' }}>
          {wecomContactsLoading ? (
            <div style={{ textAlign: 'center', padding: 24 }}>加载中...</div>
          ) : filteredWecomContacts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
              {wecomContacts.length === 0 ? '暂无客户或拉取失败' : '未找到匹配的客户'}
            </div>
          ) : (
            filteredWecomContacts.map((c) => (
              <div
                key={c.external_userid}
                onClick={() => selectWecomContact(c)}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  marginBottom: 8,
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {c.avatar ? (
                    <img src={c.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TeamOutlined style={{ color: '#999', fontSize: 18 }} />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{c.remark || c.name || c.external_userid || '-'}</div>
                    {(c.name || c.corp_name) && (
                      <div style={{ fontSize: 12, color: '#999' }}>{[c.name, c.corp_name].filter(Boolean).join(' · ')}</div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Modal>

      <Modal
        title="从企微通讯录选择成员"
        open={wecomMembersPickerVisible}
        onCancel={() => setWecomMembersPickerVisible(false)}
        footer={null}
        width={480}
        zIndex={1100}
      >
        <Input.Search
          placeholder="搜索姓名或 UserID"
          value={wecomMembersSearchText}
          onChange={(e) => setWecomMembersSearchText(e.target.value)}
          style={{ marginBottom: 12 }}
          allowClear
        />
        <div style={{ maxHeight: 320, overflow: 'auto' }}>
          {wecomMembersLoading ? (
            <div style={{ textAlign: 'center', padding: 24 }}>加载中...</div>
          ) : filteredWecomMembers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
              {wecomMembers.length === 0 ? '暂无成员或拉取失败（需应用有通讯录权限）' : '未找到匹配的成员'}
            </div>
          ) : (
            filteredWecomMembers.map((m) => (
              <div
                key={m.userid}
                onClick={() => selectWecomMember(m)}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  marginBottom: 8,
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 500 }}>{m.name || m.userid || '-'}</div>
                {m.name && m.userid && <div style={{ fontSize: 12, color: '#999' }}>{m.userid}</div>}
              </div>
            ))
          )}
        </div>
      </Modal>
    </div>
  );
};

export default MemberList;
